import * as React from 'react';
import { ReactNode, useContext, useEffect, useState } from 'react';
import { type PagedModelUsersDTO, UsersDTO } from 'api/admin/models';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { Dialog } from 'primereact/dialog';
import { DataTable, DataTablePageEvent, DataTableStateEvent } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { No, Yes } from '../../../../components/messages';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { InputText } from 'primereact/inputtext';
import { AuthContext } from '../../../../../Auth/components/AuthProvider';
import { UsersServiceApi } from '../../../../../api/admin';
import { getAuthMiddleware } from '../../../../../Auth/utils/GetAuthMiddleware';
import { parseScope } from '../shared';
import { getMandantSettingsByUsername } from '../../../../../util/mandant-settings-utils';
import { searchDelayDefault } from '../../../../../Settings/config';
import { ToastContext, ToastContextType } from '../../../../../util/providers/ToastProvider';
import { Direction } from '../../../../../util/types';

export interface OrcidFailedConnectionsProps {
  userName: string;
}

const initialLazyTableState: DataTablePageEvent = {
  first: 0,
  rows: 10,
  page: 0,
  sortField: 'surname',
  sortOrder: 1,
  filters: {},
};

export function OrcidFailedConnections(props: OrcidFailedConnectionsProps): ReactNode {
  const institution: string = getMandantSettingsByUsername(props.userName).institution;
  const [showInfo, setShowInfo] = useState<string>(null);

  const intl: IntlShape = useIntl();
  const [loading, setLoading] = useState<boolean>(false);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [users, setUsers] = useState<UsersDTO[]>([]);
  const [lazyState, setLazyState] = useState<DataTablePageEvent>(initialLazyTableState);
  const [globalFilterValue, setGlobalFilterValue] = useState<string>('');
  const toast: ToastContextType = useContext(ToastContext);

  const { actions } = useContext(AuthContext);
  const api: UsersServiceApi = new UsersServiceApi().withMiddleware(getAuthMiddleware(actions));

  useEffect((): (() => void) => {
    const delayDebounceFn = setTimeout(() => lazyLoadData(), searchDelayDefault);
    return (): void => clearTimeout(delayDebounceFn);
  }, [lazyState, globalFilterValue]);

  const lazyLoadData = (): void => {
    setLoading(true);
    void api
      .searchUserWithFailedOrcid({
        institution,
        page: lazyState.page,
        size: lazyState.rows,
        sort: lazyState.sortField || undefined,
        direction: lazyState.sortOrder > 0 ? Direction.ASC : Direction.DESC,
        searchParameter: globalFilterValue,
      })
      .then((pageUsers: PagedModelUsersDTO): void => {
        if (pageUsers) {
          setUsers(pageUsers.content ?? []);
          setTotalRecords(pageUsers.page.totalElements ?? pageUsers.content?.length ?? 0);
        }
        setLoading(false);
      })
      .catch((e): void => {
        setLoading(false);
        toast.error(e, 'message.loading.failed');
      });
  };

  const onPageChange = (e: DataTableStateEvent): void => setLazyState(e);

  const onSortChange = (e: DataTableStateEvent): void => setLazyState({ ...e, first: 0, page: 0 });

  const onFilterChange = (e: DataTableStateEvent): void => setLazyState({ ...e, first: 0 });

  const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>): void => setGlobalFilterValue(e.target.value);

  const header = (
    <div className="flex justify-content-end">
      <IconField iconPosition="left">
        <InputIcon className="pi pi-search" />
        <InputText
          value={globalFilterValue}
          onChange={onGlobalFilterChange}
          placeholder={intl.formatMessage({ id: 'search.placeholder' })}
        />
      </IconField>
    </div>
  );

  return (
    <section className="failed-connections">
      <h2>{intl.formatMessage({ id: 'orcidAdminArea.tab.failedConnections' })}</h2>
      <Dialog
        header={showInfo}
        visible={!!showInfo}
        dismissableMask={true}
        style={{ width: '50vw' }}
        breakpoints={{ '992px': '75vw', '641px': '100vw' }}
        onHide={(): void => setShowInfo(null)}
      >
        <p className="m-0">
          <FormattedMessage id={`orcid.scopes.${showInfo}`} />
        </p>
      </Dialog>
      <DataTable
        lazy={true}
        stripedRows={true}
        showGridlines={true}
        dataKey="id"
        paginator={true}
        first={lazyState.first}
        header={header}
        totalRecords={totalRecords}
        sortField={lazyState.sortField}
        sortOrder={lazyState.sortOrder}
        filters={lazyState.filters}
        loading={loading}
        rowsPerPageOptions={[10, 25, 50]}
        rows={lazyState.rows}
        globalFilterFields={['username', 'givenname', 'surname']}
        onPage={onPageChange}
        onSort={onSortChange}
        onFilter={onFilterChange}
        value={users}
      >
        <Column
          field="username"
          sortable={true}
          header={intl.formatMessage({ id: 'orcidAdminArea.header.username' })}
        ></Column>
        <Column
          field="surname"
          sortable={true}
          header={intl.formatMessage({ id: 'orcidAdminArea.header.surname' })}
        ></Column>
        <Column field="givenname" header={intl.formatMessage({ id: 'orcidAdminArea.header.givenname' })}></Column>
        <Column
          field="orcidAuthorize.orcidID"
          header={intl.formatMessage({ id: 'orcidAdminArea.header.orcidID' })}
        ></Column>
        <Column
          field="orcidAuthorize.orcidScopes"
          header={intl.formatMessage({ id: 'orcidAdminArea.header.orcidScopes' })}
          body={(usersDto: UsersDTO): ReactNode[] =>
            usersDto.orcidAuthorize.orcidScopes.map((scope) => parseScope(scope.scope, setShowInfo))
          }
        ></Column>
        <Column
          field="orcidAuthorize.publicUsageOfLimitedOrcidData"
          header={intl.formatMessage({ id: 'orcidAdminArea.header.publicUsageOfLimitedOrcidData' })}
          body={(usersDto: UsersDTO): ReactNode => (usersDto.orcidAuthorize?.publicUsageOfLimitedOrcidData ? Yes : No)}
        ></Column>
        <Column
          field="orcidAuthorize.tokenRevoked"
          header={intl.formatMessage({ id: 'orcidAdminArea.header.tokenRevoked' })}
          body={(usersDto: UsersDTO): ReactNode => (usersDto.orcidAuthorize?.tokenRevoked ? Yes : No)}
        ></Column>
      </DataTable>
    </section>
  );
}

export default OrcidFailedConnections;
